
// client/src/components/messages/MessagesBell.jsx
import React, { useEffect } from 'react';
import { FaBell } from 'react-icons/fa';
import { useMessagesUI } from '../../context/MessagesContext';
import { getSessionLocal } from '../../services/sessionLocal';
import useUnreadMessages from '../../hooks/useUnreadMessages';

/**
 * Get tenant ID from multiple sources
 * Priority: session > URL path > localStorage('me')
 */
function getTenantId() {
  // Try session first
  const session = getSessionLocal();
  let tid = session?.tenantId || session?.tenantid || session?.tenant_id;
  if (tid && Number(tid) > 0) return Number(tid);

  // Try URL path (e.g., /8/dashboard -> 8)
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const fromPath = Number(pathParts[0]);
  if (Number.isFinite(fromPath) && fromPath > 0) return fromPath;

  // Try localStorage 'me'
  try {
    const me = JSON.parse(localStorage.getItem('me') || '{}');
    tid = me?.tenantId || me?.tenantid || me?.tenant_id;
    if (tid && Number(tid) > 0) return Number(tid);
  } catch {}

  return null;
}

/**
 * Get user ID from multiple sources
 * Priority: session > localStorage('me') > window.__ME__
 */
function getUserId() {
  // Try session first
  const session = getSessionLocal();
  let uid = session?.userId || session?.userid || session?.user_id;
  if (uid && Number(uid) > 0) return Number(uid);

  // Try localStorage 'me'
  try {
    const me = JSON.parse(localStorage.getItem('me') || '{}');
    uid = me?.id || me?.userId || me?.userid || me?.user_id;
    if (uid && Number(uid) > 0) return Number(uid);
  } catch {}

  // Try window.__ME__
  if (window.__ME__) {
    uid = window.__ME__.id || window.__ME__.userId || window.__ME__.userid;
    if (uid && Number(uid) > 0) return Number(uid);
  }

  return null;
}

export default function MessagesBell({ size = 28, onBeforeOpen }) {
  const { openMessages } = useMessagesUI();

  // =====================================================
  // Extract tenant and user IDs from MULTIPLE sources
  // =====================================================
  const tenantId = getTenantId();
  const userId = getUserId();

  // =====================================================
  // Fetch unread count using the hook
  // =====================================================
  const { unreadCount, loading, error } = useUnreadMessages(tenantId, userId);
  
  const count = Number(unreadCount) || 0;
  const hasUnread = count > 0;

  // ⭐⭐⭐ DEBUG OUTPUT ⭐⭐⭐
  useEffect(() => {
    const session = getSessionLocal();
    const me = localStorage.getItem('me');

  }, [tenantId, userId, unreadCount, count, hasUnread, loading, error]);

  const handleClick = (e) => {
    e.preventDefault();
    console.log('🔔 Bell clicked!');
    if (onBeforeOpen) onBeforeOpen();
    openMessages();
  };

  const bellColor = hasUnread ? '#ff6800' : '#ffffff';
  console.log('🎨 Rendering bell with color:', bellColor);

  return (
    <button
      type="button"
      onClick={handleClick}
      title={hasUnread ? `${count} unread` : 'Messages'}
      style={{
        position: 'relative',
        background: 'none',
        border: 'none',
        padding: '10px 15px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '48px',
        minHeight: '48px',
      }}
    >
      <FaBell
        size={size}
        style={{
          color: bellColor, // ⭐ ORANGE or WHITE based on count
          transition: 'color 0.3s ease',
          filter: hasUnread 
            ? 'drop-shadow(0 0 6px rgba(255, 104, 0, 0.8))' 
            : 'none',
        }}
      />

      {hasUnread && (
        <span
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: '#ff6800',
            color: '#ffffff',
            borderRadius: '50%',
            padding: '3px 7px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            minWidth: '22px',
            height: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: '1',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}